import { useState } from 'react'
import SplunkSvg from '~/assets/splunk.svg'
import { Typography } from '~/components/common/Typography'
import { SplunkItemModal } from '~/components/modals/settings/SplunkItemModal'

interface Integration {
  conn_type: string
  connected: boolean
  metadata: unknown
}

interface ProviderDefinition {
  key: string
  connType: string
  label: string
  image?: string
  enabled: boolean
}

function CaretRightIcon() {
  return (
    <svg viewBox='0 0 16 16' fill='none' className='size-4 shrink-0'>
      <path
        d='M6 3L10 8L6 13'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function ConnectedBadge() {
  return (
    <span className='inline-flex h-[16px] items-center rounded-[50px] border border-[#36a764] bg-[#f6ffed] px-[4px]'>
      <span className='font-[Geist] text-[10px] font-bold leading-normal text-[#36a764]'>
        CONNECTED
      </span>
    </span>
  )
}

interface MonitoringConnectionsContentProps {
  workspaceUuid: string
  integrations: Integration[]
  refreshIntegrations: () => void
  isCompact?: boolean
  onConnectionSetup?: () => void
}

export function MonitoringConnectionsContent({
  workspaceUuid,
  integrations,
  refreshIntegrations,
  isCompact = false,
  onConnectionSetup
}: MonitoringConnectionsContentProps) {
  const [splunkModalOpen, setSplunkModalOpen] = useState(false)

  const splunkIntegration = integrations.find(i => i.conn_type === 'splunk_credentials')
  const splunkConnected = splunkIntegration?.connected ?? false

  const providers: ProviderDefinition[] = [
    {
      key: 'splunk',
      connType: 'splunk_credentials',
      label: 'Splunk',
      image: SplunkSvg,
      enabled: true
    }
  ]

  const handleProviderClick = (provider: ProviderDefinition) => {
    if (provider.key === 'splunk') {
      setSplunkModalOpen(true)
    }
  }

  const handleSplunkModalClose = () => {
    setSplunkModalOpen(false)
  }

  const handleSetupComplete = () => {
    setSplunkModalOpen(false)
    onConnectionSetup?.()
  }

  return (
    <>
      <div
        className={
          isCompact ? 'h-full overflow-y-auto py-4 px-3' : 'h-full overflow-y-auto py-6 px-[48px]'
        }
      >
        <div className={`flex h-full min-h-0 flex-col ${isCompact ? 'gap-4' : 'gap-8'}`}>
          {!isCompact && (
            <Typography
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='bold'
              className='shrink-0 text-[#1e1e1e]'
            >
              Connections
            </Typography>
          )}

          <div
            className={
              isCompact
                ? 'flex min-h-0 flex-1 flex-col overflow-y-auto'
                : 'flex min-h-0 flex-1 flex-wrap content-start gap-[48px_24px] justify-center overflow-y-auto'
            }
          >
            {providers.map(provider => {
              const integration = integrations.find(i => i.conn_type === provider.connType)
              const isConnected = integration?.connected ?? false
              const isClickable = provider.enabled

              return (
                <button
                  key={provider.key}
                  type='button'
                  disabled={!isClickable}
                  onClick={() => handleProviderClick(provider)}
                  className={
                    isCompact
                      ? `flex items-center gap-2.5 border-b border-[rgba(0,0,0,0.06)] px-2 py-2.5 last:border-b-0 ${isClickable ? 'cursor-pointer hover:bg-[rgba(0,0,0,0.02)]' : 'cursor-default opacity-60'}`
                      : `flex w-[400px] max-w-[400px] min-w-[320px] items-center gap-3 rounded-3xl border border-[#eaeaea] bg-[#fcfcfc] p-6 ${isClickable ? 'cursor-pointer hover:border-[#d6d6d6]' : 'cursor-default opacity-60'}`
                  }
                >
                  <div className={`flex flex-1 items-center ${isCompact ? 'gap-2.5' : 'gap-3'}`}>
                    {provider.image ? (
                      <img
                        className={`${isCompact ? 'size-6' : 'size-8'} rounded-full`}
                        src={provider.image}
                        alt={provider.label}
                      />
                    ) : (
                      <div
                        className={`flex ${isCompact ? 'size-6' : 'size-8'} items-center justify-center rounded-full bg-[rgba(0,0,0,0.25)]`}
                      />
                    )}
                    <Typography
                      fontFamily='geistMono'
                      fontSize={isCompact ? 'bodySm' : 'labelLg'}
                      fontWeight='semibold'
                      className={
                        isCompact
                          ? 'flex-1 text-left text-[rgba(36,24,42,0.92)]'
                          : 'text-[rgba(36,24,42,0.92)]'
                      }
                    >
                      {provider.label}
                    </Typography>
                    {isConnected ? <ConnectedBadge /> : null}
                  </div>
                  <CaretRightIcon />
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <SplunkItemModal
        open={splunkModalOpen}
        onClose={handleSplunkModalClose}
        refreshIntegrations={refreshIntegrations}
        metadata={splunkIntegration?.metadata ?? null}
        isEditing={splunkConnected}
        onSetupComplete={handleSetupComplete}
      />
    </>
  )
}
